import React, { memo, useMemo } from 'react';
import { connectHitInsights, connectHits, connectPagination } from 'react-instantsearch-core';
import { ProductCard } from '~/shared/components/product-card/product-card.component';
import styles from './products-grid.module.css';
import { AlgoliaHit, ProductHitsType, ProductInsightsType } from '~/features/productsList/models';
import { useFranchise } from '~/shared/utils/franchise/hooks/useFranchise';
import { FormattedAlgoliaProductType } from '../../types';
import { mapFromAlgolia } from '../../utils';
import { Headline } from '~/templates/blocks/components/Headline/Headline';
import { useTranslation } from '~/shared/utils';
import { useCookieConsent } from '~/shared/hooks/useCookieConsent/useCookieConsent';
import aa from 'search-insights';
import {
    M170SearchPage,
    P40ProductListPage,
    P41ProductListPage,
    MarketingSpot as MarketingSpotType,
} from '~/lib/data-contract';
import { useSource } from '~/shared/utils/gtm/hooks/useSource/useSource';
import { usePage, useSchemaMarkupCollectionPage } from '$templates/pages';
import clsx from 'clsx';
import { weakKey } from '~/shared/utils/jsx';
import { MarketingSpot } from '~/templates/blocks/components/marketing-spot/marketing-spot.component';
import { Pagination } from '../Pagination';

function getLayout(type?: 'p40productListPage' | 'p41productListPage' | 'm170searchPage') {
    switch (type) {
        case 'p41productListPage':
            return 'full';
        default:
            return 'with-menu';
    }
}

const ProductCardWithInsights = (props: ProductInsightsType) => {
    const { hit: product, insights } = props;
    const franchise = useFranchise();
    const { statistic } = useCookieConsent();

    const formattedProduct: FormattedAlgoliaProductType = useMemo(
        () => mapFromAlgolia(product, franchise, parseInt(product.__position)),
        [franchise, product]
    );

    return (
        <ProductCard
            product={formattedProduct}
            queryId={statistic ? product.__queryID : undefined}
            insights={insights}
            listType={product.listType}
            index={product.index}
            isProductListPage={product.isProductListPage}
        />
    );
};

const HitWithInsights = memo(connectHitInsights(aa)(ProductCardWithInsights));

/* Guards */
const isHit = (hit: AlgoliaHit | MarketingSpotType): hit is AlgoliaHit =>
    (hit as AlgoliaHit).productId !== undefined;
const isSpot = (hit: AlgoliaHit | MarketingSpotType): hit is MarketingSpotType =>
    (hit as MarketingSpotType).position !== undefined;

const CustomHit = ({ hits, isProductListPage, marketingContent }: ProductHitsType) => {
    const { type } = usePage<P40ProductListPage | P41ProductListPage | M170SearchPage>();
    const { translate } = useTranslation();
    const { source } = useSource();
    const gridLayout = getLayout(type);

    const collectionPageSchema = useSchemaMarkupCollectionPage(hits.slice(0, 8));

    // Insert marketing spots into the hits array
    const hitsWithPotientialSpots = useMemo<(AlgoliaHit | MarketingSpotType)[]>(() => {
        if (!marketingContent || marketingContent.length === 0) {
            return hits;
        }

        // Map marketing spots to their position
        // so we can do a cheap lookup
        const withKeyedPosition = marketingContent?.reduce<Record<string, MarketingSpotType>>(
            (acc, spot) => {
                acc[spot.position ?? 1] = spot;
                return acc;
            },
            {}
        );

        // Build the array with hits and marketing spots
        const formattedHits = hits.reduce((acc: (AlgoliaHit | MarketingSpotType)[], hit) => {
            let currentPosition = acc.length + 1;

            // Insert marketing spots at their position in the array
            while (
                withKeyedPosition?.[currentPosition] &&
                currentPosition <= acc.length + hits.length
            ) {
                acc.push(withKeyedPosition[currentPosition]);
                currentPosition = acc.length + 1;
            }

            // Add the hit to the array
            acc.push(hit);

            return acc;
        }, []);

        return formattedHits;
    }, [hits, marketingContent]);

    if (hits.length === 0) {
        return <Headline as="h2">{translate('commerce.noFilterResult')}</Headline>;
    }

    return (
        <>
            <div
                className={clsx(
                    gridLayout === 'full' ? styles.gridFull : styles.gridWithMenu,
                    marketingContent && marketingContent.length > 0 && styles.marketingContent
                )}
            >
                {hitsWithPotientialSpots.map((item, index) => (
                    <React.Fragment key={isHit(item) ? item.objectID : weakKey(item)}>
                        {isHit(item) && (
                            <HitWithInsights
                                hit={
                                    {
                                        ...item,
                                        // Additional data must be passed through this parameter because Algolia does not allow other props
                                        index,
                                        listType: source,
                                        isProductListPage,
                                        // Force any since AlgoliaHit is not compatible with ProductInsightsType
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    } as any
                                }
                            />
                        )}
                        {isSpot(item) && <MarketingSpot marketingSpot={item} index={index} />}
                    </React.Fragment>
                ))}
            </div>
            <Pagination />

            {collectionPageSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: collectionPageSchema }}
                />
            )}
        </>
    );
};

export const ProductsGrid = connectPagination(memo(connectHits(CustomHit)));
